/**
 * FloorLiveAdminTab
 * Whole-floor, real-time view across all four in-house games
 * (4 Hearts · Super Double · Lucky Ducky · Lucky Flamingo).
 * Backed by /api/admin/floor-live (+ /game-alerts, /floor-live/reenable).
 *
 *  · Trip-alert banner (a game hit its circuit breaker → one-tap re-enable)
 *  · Per-game LIVE cards: last-60-min & today PLAYED, rounds, players, up/down pill
 *  · Stacked PLAYED-per-hour (24h) chart across all games
 *  · Live Player Feed — who is playing right now, running wagered/won
 */
import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { GamePlayersDialog } from '../../components/gamePlayers/GamePlayersDialog';
import { Radio, Clock, AlertTriangle, Power, Users, RefreshCw, Flame, Trophy } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const money = (n) => {
    const v = Number(n || 0);
    const s = v < 0 ? '-' : '';
    return `${s}$${Math.abs(v).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};
const pct = (n) => `${Number(n || 0).toFixed(2)}%`;
const int = (n) => Number(n || 0).toLocaleString();
const timeAgo = (iso) => {
    if (!iso) return '';
    const secs = Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 1000));
    if (secs < 60) return `${secs}s ago`;
    const m = Math.floor(secs / 60);
    if (m < 60) return `${m}m ago`;
    return `${Math.floor(m / 60)}h ago`;
};

// Stacked per-hour handle chart across all games
const FloorBars = ({ buckets, games }) => {
    if (!buckets || buckets.length === 0) {
        return <div className="text-[11px] text-white/40 italic h-[150px] flex items-center justify-center">No hourly data yet.</div>;
    }
    const W = 760, H = 150, padL = 6, padR = 6, padTop = 14, padBottom = 20;
    const n = buckets.length;
    const maxHandle = Math.max(1, ...buckets.map(b => b.handle));
    const plotW = W - padL - padR;
    const plotH = H - padTop - padBottom;
    const slot = plotW / n;
    const barW = Math.max(2, slot * 0.64);
    const base = padTop + plotH;

    return (
        <svg width="100%" height={H} viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" data-testid="floor-live-hourly-chart">
            <line x1={padL} x2={W - padR} y1={base} y2={base} stroke="rgba(255,255,255,0.18)" />
            {buckets.map((b, i) => {
                const x = padL + i * slot + (slot - barW) / 2;
                const isLast = i === n - 1;
                let yCursor = base;
                return (
                    <g key={b.hour_ct}>
                        {games.map((g) => {
                            const val = Number(b[g.id] || 0);
                            if (val <= 0) return null;
                            const h = (val / maxHandle) * plotH;
                            yCursor -= h;
                            return (
                                <rect key={g.id} x={x} y={yCursor} width={barW} height={h} fill={g.accent} opacity={isLast ? 1 : 0.82} rx="1">
                                    <title>{`${b.hour_label} · ${g.label} $${val.toFixed(2)}`}</title>
                                </rect>
                            );
                        })}
                        <rect x={padL + i * slot} y={padTop} width={slot} height={plotH} fill="transparent">
                            <title>{`${b.hour_label} · Total played $${b.handle.toFixed(2)} · ${b.rounds} rounds`}</title>
                        </rect>
                        {(i % 4 === 0 || isLast) && (
                            <text x={padL + i * slot + slot / 2} y={H - 6} fontSize="9" fill="rgba(255,255,255,0.45)" textAnchor="middle">{b.hour_label}</text>
                        )}
                    </g>
                );
            })}
        </svg>
    );
};

const GameCard = ({ game, live60, today, status, onReenable, onPlayers, busy }) => {
    const l = live60 || {};
    const t = today || {};
    const up = status?.available !== false;
    return (
        <div
            className="rounded-2xl border border-white/10 bg-black/30 p-3 space-y-2"
            style={{ boxShadow: `0 0 22px -14px ${game.accent}` }}
            data-testid={`floor-game-card-${game.id}`}
        >
            <div className="flex items-center gap-2">
                <span className="inline-block w-2.5 h-2.5 rounded-sm" style={{ background: game.accent }} />
                <h3 className="text-white font-black text-sm tracking-wide truncate">{game.label}</h3>
                <button type="button" onClick={() => onPlayers(game.id)} data-testid={`floor-players-${game.id}`}
                    className="text-[10px] font-bold text-zinc-300 border border-white/15 rounded-full px-2 py-0.5 hover:bg-white/10 transition-colors">Who played</button>
                {up ? (
                    <span className="ml-auto text-[10px] font-black text-emerald-300 flex items-center gap-1" data-testid={`floor-status-${game.id}`}>
                        <span className="relative flex h-2 w-2"><span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" /><span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500" /></span>
                        LIVE
                    </span>
                ) : (
                    <button
                        onClick={() => onReenable(game.id)}
                        disabled={busy}
                        className="ml-auto text-[10px] font-black text-black bg-rose-400 hover:bg-rose-300 rounded-full px-2 py-0.5 flex items-center gap-1 disabled:opacity-50"
                        data-testid={`floor-reenable-${game.id}`}
                    >
                        <Power size={11} /> DOWN · Re-enable
                    </button>
                )}
            </div>
            <div className="grid grid-cols-2 gap-2">
                <div className="rounded-lg bg-white/5 p-2">
                    <div className="text-[9px] uppercase tracking-widest text-white/50">Played · 60 min</div>
                    <div className="text-white text-sm font-black" data-testid={`floor-${game.id}-h60`}>{money(l.handle)}</div>
                    <div className="text-[9px] text-white/40">{int(l.rounds)} rounds · {int(l.players)} players</div>
                </div>
                <div className="rounded-lg bg-white/5 p-2">
                    <div className="text-[9px] uppercase tracking-widest text-white/50">Played · Today</div>
                    <div className="font-black text-sm" style={{ color: game.accent }} data-testid={`floor-${game.id}-htoday`}>{money(t.handle)}</div>
                    <div className="text-[9px] text-white/40">{int(t.rounds)} rounds</div>
                </div>
            </div>
            <div className="flex items-center justify-between text-[10px] text-white/50 px-1">
                <span>Today net: <span className={`font-black ${(t.net || 0) >= 0 ? 'text-emerald-400' : 'text-rose-400'}`}>{money(t.net)}</span></span>
                <span>Hold {pct(t.hold_pct)}</span>
            </div>
        </div>
    );
};

export const FloorLiveAdminTab = () => {
    const [playersGame, setPlayersGame] = useState(null);
    const [live, setLive] = useState(null);
    const [alerts, setAlerts] = useState([]);
    const [bigWins, setBigWins] = useState([]);
    const [minWin, setMinWin] = useState(50);
    const [auto, setAuto] = useState(true);
    const [updatedAt, setUpdatedAt] = useState('');
    const [busy, setBusy] = useState(false);

    const loadLive = useCallback(async () => {
        try {
            const res = await axios.get(`${API}/admin/floor-live?hours=24&feed_minutes=20`, { withCredentials: true });
            setLive(res.data);
            setUpdatedAt(new Date().toLocaleTimeString());
        } catch (e) { /* silent on background poll */ }
    }, []);

    const loadBigWins = useCallback(async (amt) => {
        try {
            const res = await axios.get(`${API}/admin/floor-live/big-wins?min_amount=${amt}&hours=6&limit=40`, { withCredentials: true });
            setBigWins(res.data.wins || []);
        } catch (e) { /* silent */ }
    }, []);

    const loadAlerts = useCallback(async () => {
        try {
            const res = await axios.get(`${API}/admin/game-alerts?limit=15`, { withCredentials: true });
            setAlerts(res.data.alerts || []);
        } catch (e) { /* silent */ }
    }, []);

    useEffect(() => { loadLive(); loadAlerts(); }, [loadLive, loadAlerts]);
    useEffect(() => { loadBigWins(minWin); }, [loadBigWins, minWin]);
    useEffect(() => {
        if (!auto) return;
        const id = setInterval(() => { loadLive(); loadAlerts(); loadBigWins(minWin); }, 15000);
        return () => clearInterval(id);
    }, [auto, loadLive, loadAlerts, loadBigWins, minWin]);

    const reenable = async (gameId) => {
        setBusy(true);
        try {
            await axios.post(`${API}/admin/floor-live/reenable`, { game_id: gameId }, { withCredentials: true });
            toast.success('Game re-enabled — back on the floor');
            await loadLive();
            await loadAlerts();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Failed to re-enable');
        } finally {
            setBusy(false);
        }
    };

    const markAllRead = async () => {
        try {
            await axios.post(`${API}/admin/game-alerts/mark-read`, { all: true }, { withCredentials: true });
            await loadAlerts();
        } catch (e) { /* ignore */ }
    };

    const games = live?.games || [];
    const l60 = live?.last_60_min || {};
    const today = live?.today || {};
    const status = live?.status || {};
    const feed = live?.player_feed || [];
    const combined60 = l60.combined || {};
    const combinedToday = today.combined || {};
    const downGames = games.filter(g => status[g.id]?.available === false);

    return (
        <div className="rounded-2xl border border-white/10 bg-gradient-to-br from-[#0f0f14] to-[#050508] p-4 md:p-5 space-y-4" data-testid="floor-live-panel">
            {/* Header */}
            <div className="flex flex-wrap items-center gap-2">
                <span className="relative flex h-2.5 w-2.5">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
                    <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-emerald-500" />
                </span>
                <Radio size={18} className="text-emerald-300" />
                <h2 className="text-white text-lg font-black tracking-wide" data-testid="floor-live-title">Floor Live</h2>
                {updatedAt && <span className="text-[10px] text-white/40">updated {updatedAt}</span>}
                <div className="ml-auto flex items-center gap-2">
                    <button onClick={() => { loadLive(); loadAlerts(); }} className="flex items-center gap-1 rounded-full bg-white/10 hover:bg-white/20 px-3 py-1 text-xs text-white transition" data-testid="floor-refresh">
                        <RefreshCw size={12} /> Refresh
                    </button>
                    <button
                        onClick={() => setAuto(a => !a)}
                        className={`px-3 py-1 text-xs rounded-full transition ${auto ? 'bg-emerald-400 text-black font-black' : 'bg-white/10 text-white/70 hover:text-white'}`}
                        data-testid="floor-auto-toggle"
                    >
                        {auto ? 'Auto-refresh ON' : 'Auto-refresh OFF'}
                    </button>
                </div>
            </div>

            {/* Trip alert banner */}
            {downGames.length > 0 && (
                <div className="rounded-2xl border-2 border-rose-500/60 bg-rose-500/15 p-3 space-y-2" data-testid="floor-trip-banner">
                    <div className="flex items-center gap-2">
                        <AlertTriangle size={18} className="text-rose-300" />
                        <span className="text-rose-100 font-black text-sm">
                            {downGames.length} game{downGames.length > 1 ? 's' : ''} DOWN — circuit breaker tripped
                        </span>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                        {downGames.map(g => {
                            const s = status[g.id] || {};
                            return (
                                <div key={g.id} className="flex items-center justify-between rounded-xl bg-black/30 px-3 py-2">
                                    <div className="min-w-0">
                                        <div className="text-white font-bold text-sm truncate">{g.label}</div>
                                        <div className="text-[10px] text-rose-200/80">
                                            {s.hour_total ? `paid ${money(s.hour_total)} in an hour (limit ${money(s.limit)})` : 'auto-disabled by safety limit'}
                                            {s.tripped_at ? ` · ${timeAgo(s.tripped_at)}` : ''}
                                        </div>
                                    </div>
                                    <button
                                        onClick={() => reenable(g.id)}
                                        disabled={busy}
                                        className="shrink-0 flex items-center gap-1 rounded-full bg-emerald-400 hover:bg-emerald-300 text-black font-black text-xs px-3 py-1.5 disabled:opacity-50"
                                        data-testid={`floor-banner-reenable-${g.id}`}
                                    >
                                        <Power size={12} /> Re-enable
                                    </button>
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}

            {/* Big-Win ticker */}
            <div className="rounded-2xl border border-amber-400/30 bg-gradient-to-br from-amber-500/10 to-rose-500/5 p-3" data-testid="floor-bigwin-ticker">
                <div className="flex flex-wrap items-center gap-2 mb-2">
                    <Trophy size={16} className="text-amber-300" />
                    <h3 className="text-white text-sm font-black tracking-wide">Big Wins</h3>
                    <span className="text-[10px] text-white/40">last 6h · ≥ ${minWin}</span>
                    <div className="ml-auto flex items-center gap-1 rounded-full bg-white/5 p-1">
                        {[25, 50, 100, 250].map(v => (
                            <button
                                key={v}
                                onClick={() => setMinWin(v)}
                                className={`px-2.5 py-0.5 text-[11px] rounded-full transition ${minWin === v ? 'bg-amber-300 text-black font-black' : 'text-white/70 hover:text-white'}`}
                                data-testid={`floor-bigwin-min-${v}`}
                            >${v}+</button>
                        ))}
                    </div>
                </div>
                {bigWins.length === 0 ? (
                    <div className="text-white/40 text-xs italic px-2 py-3">No wins ≥ ${minWin} in the last 6 hours.</div>
                ) : (
                    <div className="flex flex-col gap-1.5 max-h-64 overflow-y-auto" data-testid="floor-bigwin-list">
                        {bigWins.map((w, i) => {
                            const isHuge = w.tier === 'huge';
                            const isBig = w.tier === 'big';
                            const border = isHuge ? 'border-amber-400/70' : isBig ? 'border-rose-400/50' : 'border-white/10';
                            const bg = isHuge ? 'bg-amber-500/15' : isBig ? 'bg-rose-500/10' : 'bg-white/5';
                            return (
                                <div key={w.id} className={`flex items-center gap-2 rounded-xl border ${border} ${bg} px-3 py-1.5 ${i === 0 ? 'ring-1 ring-amber-300/40' : ''}`} data-testid={`floor-bigwin-${w.id}`}>
                                    {isHuge ? <Flame size={14} className="text-amber-300 shrink-0" /> : <span className="inline-block w-2.5 h-2.5 rounded-sm shrink-0" style={{ background: w.accent }} />}
                                    <span className="text-white font-semibold text-[12px] truncate max-w-[130px]">{w.username}</span>
                                    <span className="text-white/50 text-[11px] truncate">{w.game}</span>
                                    {isHuge && <span className="text-[9px] font-black text-black bg-amber-300 rounded px-1">HUGE</span>}
                                    {isBig && <span className="text-[9px] font-black text-black bg-rose-300 rounded px-1">BIG</span>}
                                    <span className={`ml-auto font-black text-sm shrink-0 ${isHuge ? 'text-amber-300' : isBig ? 'text-rose-300' : 'text-emerald-300'}`}>{money(w.amount)}</span>
                                    <span className="text-[10px] text-white/40 shrink-0 w-14 text-right">{timeAgo(w.created_ct)}</span>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>


            {/* Combined banner */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                <div className="rounded-xl bg-white/5 p-3 text-center">
                    <div className="text-[10px] uppercase tracking-widest text-white/50">Played · last 60 min</div>
                    <div className="text-white text-lg font-black" data-testid="floor-combined-60-handle">{money(combined60.handle)}</div>
                    <div className="text-[10px] text-white/40">{int(combined60.rounds)} rounds</div>
                </div>
                <div className="rounded-xl bg-white/5 p-3 text-center">
                    <div className="text-[10px] uppercase tracking-widest text-white/50">Played · today</div>
                    <div className="text-amber-300 text-lg font-black" data-testid="floor-combined-today-handle">{money(combinedToday.handle)}</div>
                    <div className="text-[10px] text-white/40">{int(combinedToday.rounds)} rounds</div>
                </div>
                <div className="rounded-xl bg-white/5 p-3 text-center">
                    <div className="text-[10px] uppercase tracking-widest text-white/50">Net · today</div>
                    <div className={`text-lg font-black ${(combinedToday.net || 0) >= 0 ? 'text-emerald-400' : 'text-rose-400'}`} data-testid="floor-combined-today-net">{money(combinedToday.net)}</div>
                </div>
                <div className="rounded-xl bg-white/5 p-3 text-center">
                    <div className="text-[10px] uppercase tracking-widest text-white/50">Hold · today</div>
                    <div className="text-white text-lg font-black" data-testid="floor-combined-today-hold">{pct(combinedToday.hold_pct)}</div>
                </div>
            </div>

            {/* Per-game cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-3">
                {games.map(g => (
                    <GameCard
                        key={g.id}
                        game={g}
                        live60={l60.per_game?.[g.id]}
                        today={today.per_game?.[g.id]}
                        status={status[g.id]}
                        onReenable={reenable}
                        onPlayers={setPlayersGame}
                        busy={busy}
                    />
                ))}
            </div>
            <GamePlayersDialog gameId={playersGame} onClose={() => setPlayersGame(null)} />

            {/* Hourly chart */}
            <div className="rounded-2xl bg-black/30 border border-white/10 p-3">
                <div className="flex flex-wrap items-center justify-between gap-2 mb-1">
                    <div className="text-[11px] uppercase tracking-widest text-white/60">Played per hour · whole floor (last 24h)</div>
                    <div className="flex flex-wrap items-center gap-3 text-[10px]">
                        {games.map(g => (
                            <span key={g.id} className="flex items-center gap-1">
                                <span className="inline-block w-2.5 h-2.5 rounded-sm" style={{ background: g.accent }} /> {g.label}
                            </span>
                        ))}
                    </div>
                </div>
                <FloorBars buckets={live?.hourly} games={games} />
            </div>

            {/* Live player feed */}
            <div className="rounded-2xl border border-white/10 bg-black/30 p-3">
                <div className="flex items-center gap-2 mb-2">
                    <Users size={16} className="text-cyan-300" />
                    <h3 className="text-white text-sm font-black tracking-wide">Playing Now</h3>
                    <span className="text-[10px] text-white/40 flex items-center gap-1"><Clock size={11} /> last {live?.feed_minutes || 20} min</span>
                    <span className="ml-auto text-[10px] text-white/50">{feed.length} active</span>
                </div>
                {feed.length === 0 ? (
                    <div className="text-white/40 text-xs italic px-2 py-4">Nobody is playing right now.</div>
                ) : (
                    <table className="w-full text-xs" data-testid="floor-player-feed">
                        <thead>
                            <tr className="text-white/50">
                                <th className="text-left py-1">Player</th>
                                <th className="text-left py-1">Games</th>
                                <th className="text-right py-1">Wagered</th>
                                <th className="text-right py-1">Won</th>
                                <th className="text-right py-1">Net</th>
                                <th className="text-right py-1">Rounds</th>
                                <th className="text-right py-1">Last</th>
                            </tr>
                        </thead>
                        <tbody>
                            {feed.map((p) => (
                                <tr key={p.user_id} className="border-t border-white/5" data-testid={`floor-feed-row-${p.user_id}`}>
                                    <td className="py-1 text-white font-semibold truncate max-w-[140px]">{p.username}</td>
                                    <td className="py-1 text-white/60 truncate max-w-[160px]">{p.games.join(', ')}</td>
                                    <td className="py-1 text-right text-white/80">{money(p.wagered)}</td>
                                    <td className="py-1 text-right text-white/80">{money(p.won)}</td>
                                    <td className={`py-1 text-right font-black ${p.net >= 0 ? 'text-emerald-400' : 'text-rose-400'}`}>{money(p.net)}</td>
                                    <td className="py-1 text-right text-white/60">{int(p.rounds)}</td>
                                    <td className="py-1 text-right text-white/40">{timeAgo(p.last_ct)}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>

            {/* Trip alert log */}
            <div className="rounded-2xl border border-white/10 bg-black/30 p-3">
                <div className="flex items-center gap-2 mb-2">
                    <AlertTriangle size={16} className="text-amber-300" />
                    <h3 className="text-white text-sm font-black tracking-wide">Circuit-Breaker Trip Log</h3>
                    {alerts.some(a => !a.read) && (
                        <button onClick={markAllRead} className="ml-auto text-[10px] rounded-full bg-white/10 hover:bg-white/20 px-2 py-0.5 text-white/70" data-testid="floor-mark-alerts-read">
                            Mark all read
                        </button>
                    )}
                </div>
                {alerts.length === 0 ? (
                    <div className="text-white/40 text-xs italic px-2 py-3">No circuit-breaker trips recorded. All good.</div>
                ) : (
                    <div className="space-y-1.5">
                        {alerts.map(a => (
                            <div key={a.id} className={`flex items-center gap-2 rounded-lg px-3 py-1.5 ${a.read ? 'bg-white/5' : 'bg-amber-500/10 border border-amber-500/30'}`} data-testid={`floor-alert-${a.id}`}>
                                {!a.read && <span className="inline-block w-1.5 h-1.5 rounded-full bg-amber-400" />}
                                <span className="text-white/80 text-[11px] truncate">{a.message}</span>
                                <span className="ml-auto text-[10px] text-white/40 shrink-0">{timeAgo(a.created_ct)}</span>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};

export default FloorLiveAdminTab;
